import React, { useState } from "react";
import PropTypes from "prop-types";

import StatusSelect from "modules/appointments/components/StatusSelect";
import {
  STATUS_LABEL,
  STATUS_TONE,
  END_STATUSES,
} from "modules/appointments/helper/appointmentConstants";
import { formatTimeSlot, formatDate } from "modules/appointments/helper/formatters";
import AppointmentDocumentsMenu from "modules/patient/components/AppointmentDocumentsMenu";
import DocumentsMenu from "modules/patient/components/DocumentsMenu";
import UpdateBookingFor from "modules/patient/components/UpdateBookingFor";

import MOBILE from "../mobileConfig";
import BottomSheet from "./BottomSheet";
import { CalendarIcon } from "./MobileApptStrip";

/**
 * The appointment in full — Figma 5477:2.
 *
 * Everything the desktop header card spreads across its second and third strips
 * (`.ph-appointment` and the touchpoints band), stacked to fit 360px. Same
 * data, same components: the two document menus are the real ones, the status
 * control is the same StatusSelect, and Reschedule applies the same
 * "not when blocked" rule.
 *
 * All of those open portalled menus and dialogs from inside a sheet that is
 * itself portalled. That works because every one of them portals to <body>
 * rather than rendering in place, so none is trapped by the sheet — the same
 * reason ModalPortal exists for the desktop rail's sticky columns.
 */

const Field = ({ label, className, children }) => (
  <div className={`zym-af${className ? ` ${className}` : ""}`}>
    <span className="zym-af-label">{label}</span>
    <div className="zym-af-value">{children}</div>
  </div>
);
Field.propTypes = {
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node,
};

/** Figma 5477:2 — the sheet of paper on the Call note button. */
const NoteIcon = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M9.2 1.8H4a1.5 1.5 0 00-1.5 1.5v9.4A1.5 1.5 0 004 14.2h8a1.5 1.5 0 001.5-1.5V6.1z" />
    <path d="M9.2 1.8v4.3h4.3M5.4 9h5.2M5.4 11.4h3.4" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M5 1.9 6.2 4.5 5 5.9a8.3 8.3 0 0 0 4.3 4.3l1.4-1.2 2.6 1.2v2.7a.9.9 0 0 1-1 .9A12.2 12.2 0 0 1 2.2 3.9a.9.9 0 0 1 .9-1H5Z" />
  </svg>
);

/**
 * One touchpoint line — the newest appointment, then the rest behind "+N more".
 *
 * The newest opens on its own tap; "+N more" hands the whole list to a sheet of
 * its own. An earlier attempt expanded the rest inline, which pushed everything
 * below it down the sheet and made the list hard to read against. Desktop
 * answers this with a hover popover; the mobile equivalent of a popover is
 * another sheet.
 */
const Touchpoint = ({ rows, state, empty, onOpen, onShowAll }) => {
  if (state === "loading") return <span className="zym-af-note">Loading…</span>;
  /* A failed fetch is not an empty result — desktop's cell says so too. */
  if (!rows || rows.length === 0)
    return (
      <span className="zym-af-note">{state === "error" ? "Couldn\u2019t load" : empty}</span>
    );

  /* toTouchpoints already formats these — { id, name, date }, with name
     defaulted to "Consultation" and date run through formatLongDate
     (touchpoints.js:33-35). Nothing to re-derive here. */
  const [first, ...rest] = rows;

  return (
    <span className="zym-tp">
      <span className="zym-tp-line">
        <button type="button" className="zym-tp-name" onClick={() => onOpen(first.id)}>
          {first.name}
        </button>
        <span className="zym-tp-date">{first.date}</span>
      </span>

      {rest.length > 0 && (
        <button type="button" className="zym-tp-more" onClick={onShowAll}>
          +{rest.length} more
        </button>
      )}
    </span>
  );
};
Touchpoint.propTypes = {
  rows: PropTypes.array,
  state: PropTypes.string,
  empty: PropTypes.string.isRequired,
  onOpen: PropTypes.func.isRequired,
  onShowAll: PropTypes.func.isRequired,
};

/** The full list, in a sheet of its own — one row per appointment. */
const TouchpointList = ({ open, title, rows, onOpen, onClose }) => (
  <BottomSheet
    open={open}
    onClose={onClose}
    title={title}
    className="zym-sheet-tp"
    /* Over the appointment sheet it opened from, not beside it. */
    nested
  >
    {(rows || []).map((r) => (
      <button
        type="button"
        className="zym-tp-row"
        key={r.id}
        onClick={() => onOpen(r.id)}
      >
        <span className="zym-tp-row-name">{r.name}</span>
        {/* Upcoming rows carry their start time, as desktop's dropdown does
            (touchpoints.js sets `time` for upcoming only). */}
        <span className="zym-tp-row-date">{r.time ? `${r.date} · ${r.time}` : r.date}</span>
        <svg className="zym-tp-row-chev" viewBox="0 0 12 12" aria-hidden="true">
          <path
            d="M4 2l4 4-4 4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    ))}
  </BottomSheet>
);
TouchpointList.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  rows: PropTypes.array,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const MobileApptSheet = ({
  open,
  onClose,
  appointment,
  patient,
  reasonName,
  updating,
  onChangeStatus,
  onReschedule,
  rescheduleDisabledReason,
  onUpdateBookingFor,
  bookingForBlockedReason,
  onOpenCallNote,
  pastAppointments,
  upcomingAppointments,
  touchpointsState,
  onOpenAppointment,
  onOpenAppointmentDetails,
  canPrescribe,
  onCreatePrescription,
  onCreateHealthPlan,
  call,
}) => {
  /* Which touchpoint list is open, if either. */
  const [listing, setListing] = useState(null);

  if (!appointment) return null;

  const when = [
    formatDate(appointment.appointmentDate),
    formatTimeSlot(appointment.timeSlot, appointment.serviceDuration),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Appointment details"
      className="zym-sheet-appt"
      clearTopBar
      footer={
        <>
          {/* One or the other, never both — a doctor prescribes, every other
              clinician category writes a health plan. */}
          {canPrescribe ? (
            <button type="button" className="zym-btn is-primary" onClick={onCreatePrescription}>
              Create Rx
            </button>
          ) : (
            <button type="button" className="zym-btn is-primary" onClick={onCreateHealthPlan}>
              Create health plan
            </button>
          )}
          {call && (
            <button
              type="button"
              className="zym-btn is-accent"
              disabled={!call.canCallPrimary}
              onClick={call.callPrimary}
              title={call.callPrimaryTitle}
            >
              <PhoneIcon />
              Call patient
            </button>
          )}
        </>
      }
    >
      <p className="zym-appt-when-big">
        <CalendarIcon />
        {when || "—"}
      </p>

      {/* Full width here, where row 2 has it inline — this is the sheet that is
          about the appointment, so its state is the first thing to act on. */}
      <div className="zym-appt-status-wide">
        <StatusSelect
          value={appointment.status}
          tone={STATUS_TONE[appointment.status] || "progress"}
          label={STATUS_LABEL[appointment.status] || "—"}
          labels={STATUS_LABEL}
          tones={STATUS_TONE}
          disabled={updating}
          readOnly={END_STATUSES.indexOf(appointment.status) !== -1}
          onChange={onChangeStatus}
        />
      </div>

      <div className="zym-af-grid">
        <Field label="Appt ID">
          {/* The appointment-details screen, /navigo-module/appointment/:id —
              the same destination the desktop header's Appt ID links to. Shuts
              the sheet on the way out so returning with Back does not land on a
              screen with a sheet still open over it. */}
          <button
            type="button"
            className="zym-link"
            onClick={() => {
              onClose();
              onOpenAppointmentDetails();
            }}
          >
            {appointment.id}
          </button>
        </Field>
        <Field label="Account code">{appointment.accountCode || "—"}</Field>
      </div>

      <Field label="Account name">{appointment.accountName || "—"}</Field>

      <Field label="Reason">
        {/* reasonId only on the wire — the name comes from the master list the
            screen already fetched; reasonDetails is the patient's own text. */}
        {[reasonName, appointment.reasonDetails].filter(Boolean).join(" — ") || "—"}
      </Field>

      <Field label="Appointment documents">
        <AppointmentDocumentsMenu appointmentId={appointment.id} />
      </Field>

      <hr className="zym-af-rule" />

      <Field label="Past appointments">
        <Touchpoint
          rows={pastAppointments}
          state={touchpointsState}
          empty="No past appointments"
          onOpen={onOpenAppointment}
          onShowAll={() => setListing("past")}
        />
      </Field>

      <Field label="Upcoming appointments">
        <Touchpoint
          rows={upcomingAppointments}
          state={touchpointsState}
          empty="None scheduled"
          onOpen={onOpenAppointment}
          onShowAll={() => setListing("upcoming")}
        />
      </Field>

      {patient && patient.id && (
        <Field label="All documents">
          {/* The patient-wide list, a different endpoint from the appointment's
              own above. Both menus portal, so both open over the sheet. */}
          <DocumentsMenu patientId={patient.id} />
        </Field>
      )}

      <div className="zym-af-actions">
        {/* Bridge's rule, unchanged: a blocked, gym or closed appointment gets
            NO button at all rather than a disabled one. */}
        {!rescheduleDisabledReason && onReschedule && (
          <button type="button" className="zym-btn is-soft" onClick={onReschedule}>
            Reschedule
          </button>
        )}
        <button type="button" className="zym-btn" onClick={onOpenCallNote}>
          <NoteIcon />
          Call note
        </button>

        {/* THE DESKTOP CONTROL, MOUNTED HERE — not a mobile rebuild of it.
            UpdateBookingFor owns the family list, the confirmation, the
            blocked-reason handling and the focus trap; the screen passes the
            same `onUpdateBookingFor` the desktop header is given, so the save
            and the remount that follows it are the ones already in place.

            It sits in the sheet because that is where this screen keeps
            everything about the appointment — desktop's home for it is the
            patient header's field strip, which mobile does not draw.

            Its popover and confirmation both portal to <body>, so they open
            over the sheet rather than inside its scroll box; mobile.scss sizes
            them. A row of its own: the label does not fit beside Call note. */}
        {MOBILE.updateBookingFor && onUpdateBookingFor && patient && appointment ? (
          <div className="zym-af-booking">
            <UpdateBookingFor
              appointment={appointment}
              currentMemberId={patient.id}
              onConfirm={onUpdateBookingFor}
              blockedReason={bookingForBlockedReason}
            />
          </div>
        ) : null}
      </div>

      <TouchpointList
        open={listing !== null}
        title={listing === "upcoming" ? "Upcoming appointments" : "Past appointments"}
        rows={listing === "upcoming" ? upcomingAppointments : pastAppointments}
        onOpen={onOpenAppointment}
        onClose={() => setListing(null)}
      />
    </BottomSheet>
  );
};

MobileApptSheet.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  appointment: PropTypes.object,
  patient: PropTypes.object,
  reasonName: PropTypes.string,
  updating: PropTypes.bool,
  onChangeStatus: PropTypes.func.isRequired,
  onReschedule: PropTypes.func,
  rescheduleDisabledReason: PropTypes.string,
  /** "Update booking for" — (member) => Promise. Omit and no control is drawn. */
  onUpdateBookingFor: PropTypes.func,
  /** When set, the control is held and this says why. */
  bookingForBlockedReason: PropTypes.string,
  onOpenCallNote: PropTypes.func.isRequired,
  pastAppointments: PropTypes.array,
  upcomingAppointments: PropTypes.array,
  touchpointsState: PropTypes.string,
  onOpenAppointment: PropTypes.func.isRequired,
  onOpenAppointmentDetails: PropTypes.func.isRequired,
  canPrescribe: PropTypes.bool,
  onCreatePrescription: PropTypes.func,
  onCreateHealthPlan: PropTypes.func,
  call: PropTypes.object,
};

export default MobileApptSheet;
